import { Link } from 'react-router-dom'
import { MapPin, Utensils, Cake, ArrowRight } from 'lucide-react'
import useInView from '../../hooks/useInView'

const BRANCHES = [
  {
    name: 'Desres Kitchen',
    tagline: 'Authentic South African Cuisine',
    location: 'Limpopo, South Africa',
    description: 'Our flagship branch, running for over 25 years. Serving authentic African cuisine, daily bakes, sweet treats, and customised cakes.',
    highlights: ['Mukokoroshi & Steak Platters', 'Mala A Khuhu & Chicken Liver', 'Fresh Bread & Scones', 'Sweet Treats & Customised Cakes', 'Burgers, Russians & More'],
    icon: Utensils,
    menuLink: '/menu?branch=desres',
  },
  {
    name: "Yahweh's Delight",
    tagline: 'Freshly Baked, Daily',
    location: 'Pretoria, South Africa',
    description: 'Our newly opened bakery branch in Pretoria, bringing the same family tradition to a new community. Freshly baked bread, scones, fatcook, rolls, sandwiches, and kotas.',
    highlights: ['Fresh Bread (Brown & White)', 'Scones & Fatcook', 'School Buns', 'Sandwiches, Kotas & Toast', 'Hotdog & Russian Rolls'],
    icon: Cake,
    menuLink: '/menu?branch=yawehs',
  },
]

function ScrollReveal({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  )
}

export default function Home() {
  return (
    <div>
      {/* Hero — charcoal bg with grid lines and warm glow */}
      <section className="bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(pct => (
          <div key={pct} className="absolute top-0 bottom-0 w-px bg-border-dark" style={{ left: `${pct}%` }} />
        ))}
        {/* Warm glow */}
        <div className="absolute top-1/2 left-1/3 w-96 h-96 -translate-x-1/2 -translate-y-1/2 bg-terracotta/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-28 sm:py-40 text-center">
          <ScrollReveal delay={0.1}>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-6 h-0.5 bg-terracotta" />
              <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Est. 25+ Years</span>
              <div className="w-6 h-0.5 bg-terracotta" />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <h1 className="font-display text-cream tracking-[0.04em] leading-[0.95] font-extrabold" style={{ fontSize: 'clamp(72px, 11vw, 160px)' }}>
              DESRES KITCHEN
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <p className="font-display text-terracotta tracking-[0.06em] leading-[0.95] mt-2" style={{ fontSize: 'clamp(24px, 3vw, 40px)', WebkitTextStroke: '1px #E8761A', color: 'transparent' }}>
              & CONFECTIONERY
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.4}>
            <p className="text-sm text-muted max-w-xl mx-auto mt-8 leading-relaxed">
              A family business with over 25 years of tradition. African cuisine, daily bakes,
              sweet treats, and customised cakes — serving our communities with love.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.5}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link
                to="/menu"
                className="inline-flex items-center justify-center gap-2 bg-terracotta text-white font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 rounded-full hover:bg-terracotta-glow hover:-translate-y-0.5 transition-all"
              >
                <Utensils size={16} /> View Our Menu
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-cream border border-border-dark font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 rounded-full hover:border-terracotta hover:text-terracotta transition-all"
              >
                <MapPin size={16} /> Find Us
              </Link>
            </div>
          </ScrollReveal>
          {/* Scroll indicator */}
          <div className="mt-16 flex justify-center">
            <div className="w-px h-12 bg-terracotta/40" style={{ animation: 'livePulse 2s ease-in-out infinite' }} />
          </div>
        </div>
      </section>

      {/* Gradient border */}
      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {/* Branches */}
      <section className="bg-umber">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-6 h-0.5 bg-terracotta" />
              <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Our Locations</span>
            </div>
            <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95] mb-12 font-extrabold" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
              Two Branches
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BRANCHES.map((branch, i) => {
              const Icon = branch.icon
              return (
                <ScrollReveal key={branch.name} delay={0.2 + i * 0.15}>
                  <Link
                    to={branch.menuLink}
                    className="group relative block bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:border-terracotta/40 transition-colors"
                  >
                    <div className="p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-surface-high rounded-xl flex items-center justify-center">
                          <Icon size={22} className="text-ink-mid" />
                        </div>
                        <div>
                          <h3 className="font-display text-cream text-xl tracking-wide">{branch.name}</h3>
                          <p className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">{branch.tagline}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted mb-4">
                        <MapPin size={14} /> {branch.location}
                      </div>
                      <p className="text-sm text-ink-mid mb-6 leading-relaxed">{branch.description}</p>
                      <div className="space-y-2 mb-6">
                        {branch.highlights.map((item, j) => (
                          <div key={j} className="flex items-center gap-2 text-sm text-cream">
                            <span className="w-1.5 h-1.5 bg-terracotta rounded-full" />
                            {item}
                          </div>
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-1.5 font-body text-[13px] font-bold tracking-[0.1em] uppercase text-terracotta group-hover:-translate-x-0 translate-x-0 transition-transform">
                        View Menu <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Gradient border */}
      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {/* CTA */}
      <section className="bg-charcoal">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28 text-center">
          <ScrollReveal>
            <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95] mb-4 font-extrabold" style={{ fontSize: 'clamp(36px, 5vw, 64px)' }}>
              Hungry Yet?
            </h2>
            <p className="text-sm text-muted mb-10 max-w-lg mx-auto">Check out our full menu or come visit us at one of our branches.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/menu"
                className="inline-flex items-center justify-center gap-2 bg-terracotta text-white font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 rounded-full hover:bg-terracotta-glow hover:-translate-y-0.5 transition-all"
              >
                Full Menu <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-cream border border-border-dark font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 rounded-full hover:border-terracotta hover:text-terracotta transition-all"
              >
                Directions & Hours
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Gradient border */}
      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {/* Operating hours */}
      <section className="bg-umber">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-6 h-0.5 bg-terracotta" />
              <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Visit Us</span>
            </div>
            <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95] mb-12 font-extrabold" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
              Hours
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            {[
              { name: 'Desres Kitchen', Icon: Utensils, hours: [['Monday - Saturday', '07:00 - 18:00'], ['Sunday', '08:00 - 15:00']] },
              { name: "Yahweh's Delight", Icon: Cake, hours: [['Monday - Saturday', '06:00 - 18:00'], ['Sunday', '07:00 - 14:00']] },
            ].map((b, i) => (
              <ScrollReveal key={b.name} delay={0.2 + i * 0.15}>
                <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)]">
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <b.Icon size={18} className="text-ink-mid" />
                      <h3 className="font-display text-cream text-lg tracking-wide">{b.name}</h3>
                    </div>
                    <div className="space-y-2">
                      {b.hours.map(([days, time]) => (
                        <div key={days} className="flex justify-between text-sm">
                          <span className="text-muted">{days}</span>
                          <span className="text-cream font-body font-medium">{time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
